import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { getValueColumns } from 'app/util/queryResults';
import { Box, Flex, HTMLTable, Icon, Text } from 'core/components';
import { deviceNameRenderer, valueOrNameRenderer } from 'app/components/dataviews/views/legend/legendRenderers';
import { findMetricOption, interfaceMetrics } from 'app/components/dataviews/views/legend/legendUtils';

import { getMetricRenderer, mergeColumns } from 'app/components/resultsTable/util';
import Dimension from './Dimension';
import Header from './Header';
import Row from './Row';

/**
 * @class ResultsTable
 * @extends {import('react').Component<import('./types.d.ts').ResultsTableProps>}
 * @param {import('./types.d.ts').ResultsTableVProps} props
 * @deprecated uses separate renderer from standardized Table/VirtualizedTable. Prefer ResultsTableV2 if possible (although the interfaces are not perfectly 1:1)
 */
@inject('$dictionary')
@observer
class ResultsTable extends Component {
  /**
   * @static
   * @type {Partial<import('./types.d.ts').ResultsTableV2Props>}
   */
  static defaultProps = {
    showSparklines: true,
    showDeviceForInterface: true,
    showActionsMenu: false,
    showRefresh: false,
    showLastUpdated: false,
    showCombinedTotalColumn: false,
    showLastDatapoints: false,
    striped: true,
    interactive: false,
    emptyState: null,
    bordered: false,
    condensed: false,
    stickyHeader: false,
    limit: undefined,
    dimensionOverrides: [],
    valueOverrides: [],
    mergeOverlayCells: false
  };

  /**
   * @static
   * @param {import('./types.d.ts').ResultsTableV2Props} props
   * @returns {{results: import('./types.d.ts').QueryResultsCollection}}
   */
  static getDerivedStateFromProps(props) {
    const { queryResultsCollection, limit } = props;

    if (!queryResultsCollection) {
      return { results: [] };
    }

    if (!queryResultsCollection.sortState.field) {
      queryResultsCollection.sort(props.queryModel.get('outsort'));
    }

    return {
      results: queryResultsCollection.slice(0, limit)
    };
  }

  state = {};

  /**
   * @param {string} metric
   * @returns {import('./types.d.ts').MetricsColumn}
   */
  getMetricColumn(metric) {
    const { $dictionary } = this.props;
    const metricColumns = $dictionary.get('metricColumns');
    const metricOption = findMetricOption(metric);

    if (!metricOption) {
      return {
        name: metricColumns[metric] || metric,
        key: metricColumns[metric] || metric,
        label: metricColumns[metric] || metric,
        metric
      };
    }

    const { label, group, tagLabel } = metricOption;

    return {
      name: metricColumns[metric] || metric,
      key: metricColumns[metric] || metric,
      metric,
      label: <Dimension group={group} label={tagLabel || label} />
    };
  }

  /**
   * @param {import('./types.d.ts').QueryModel} queryModel
   * @param {import('./types.d.ts').Bucket} bucket
   * @returns {import('./types.d.ts').Column[]}
   */
  getDimensionColumns(queryModel, bucket) {
    const { $dictionary } = this.props;
    const metricColumns = $dictionary.get('metricColumns');
    const metrics = bucket.firstQuery.get('metric');
    const fastData = bucket.firstQuery.get('fastData');
    const filterDimensionsEnabled = bucket.firstQuery.get('filterDimensionsEnabled');

    let dimensionColumns = [];

    if (filterDimensionsEnabled) {
      const filterDimensionName = bucket.firstQuery.get('filterDimensionName');

      dimensionColumns.push({
        name: metricColumns.Traffic,
        label: (
          <>
            <Text muted as="div" fontSize="small" fontWeight="normal">
              Filter-Based
            </Text>
            {filterDimensionName}
          </>
        ),
        renderer: valueOrNameRenderer(false)
      });
    } else {
      metrics.forEach((metric, index) => {
        let column = this.getMetricColumn(metric);

        if (queryModel.get('aggregateFiltersEnabled')) {
          // Find the base metric information from which to derive renderers
          const [filter] = queryModel.get('aggregateFilters');
          if (filter.metric) {
            [metric] = filter.metric;
            column = this.getMetricColumn(metric);
          }

          // Update the place in which to look for key if provided
          const aggregateFiltersDimensionLabel = queryModel.get('aggregateFiltersDimensionLabel');
          if (aggregateFiltersDimensionLabel) {
            column.key = aggregateFiltersDimensionLabel;
            column.name = aggregateFiltersDimensionLabel;
            column.label = aggregateFiltersDimensionLabel;
          }
        }

        column.renderer = getMetricRenderer({ metric, firstQuery: bucket.firstQuery, index, fastData });

        dimensionColumns.push(column);
      });
    }

    if (this.shouldAddDeviceColumn(dimensionColumns)) {
      let deviceColumn = 'i_device_id';

      if (metrics.includes('bgp_ult_exit_interface')) {
        deviceColumn = 'i_ult_exit_device_name';
      }

      dimensionColumns = [
        {
          ...this.getMetricColumn(deviceColumn),
          renderer: deviceNameRenderer()
        },
        ...dimensionColumns
      ];
    }

    return dimensionColumns;
  }

  shouldAddDeviceColumn(dimensionColumns) {
    const { showDeviceForInterface } = this.props;
    return showDeviceForInterface && dimensionColumns.some((column) => interfaceMetrics.includes(column.metric));
  }

  getRowKey(row, dimensionColumns) {
    if (this.shouldAddDeviceColumn(dimensionColumns)) {
      return `${row.get('key')}-${row.get('i_device_name')}`;
    }

    return `${row.get('key')}-${row.id}`;
  }

  handleColumnClick = (event) => {
    const { queryResultsCollection, limit, onSort } = this.props;
    const { currentTarget } = event;
    const { dataset } = currentTarget || {};
    const { column } = dataset;

    queryResultsCollection.sort(column);

    if (onSort) {
      onSort(queryResultsCollection.sortState);
    }

    this.setState({
      results: queryResultsCollection.slice(0, limit)
    });
  };

  renderNoResults(emptyStateColSpan) {
    const { emptyState } = this.props;

    if (emptyState) {
      return (
        <tbody>
          <tr>
            <td colSpan={emptyStateColSpan}>{emptyState}</td>
          </tr>
        </tbody>
      );
    }
    return (
      <tbody>
        <tr>
          <Box as="td" colSpan={emptyStateColSpan} p={4}>
            <Flex alignItems="center" justifyContent="center">
              <Icon icon="folder-close" mr={1} color="muted" iconSize={16} />
              <Text color="muted" fontStyle="italic" fontWeight="bold">
                No results
              </Text>
            </Flex>
          </Box>
        </tr>
      </tbody>
    );
  }

  render() {
    const {
      dimensionOverrides,
      valueOverrides,
      queryResultsCollection,
      showCombinedTotalColumn,
      showLastDatapoints,
      showActionsMenu,
      showSparklines,
      striped,
      interactive,
      bordered,
      condensed,
      stickyHeader,
      loading,
      queryModel,
      bucket,
      actions,
      allowSelection,
      mergeOverlayCells,
      tableClass
    } = this.props;
    const { results } = this.state;

    const placeholders = [...new Array(8)];
    const dimensionColumns = mergeColumns(this.getDimensionColumns(queryModel, bucket), dimensionOverrides).filter(
      (c) => !c.hidden
    );
    const valueColumns = mergeColumns(
      getValueColumns({ queryModel, bucket, showCombinedTotalColumn, showLastDatapoints, queryResultsCollection }),
      valueOverrides
    );

    const emptyStateColSpan =
      (allowSelection ? 1 : 0) + dimensionColumns.length + valueColumns.length + (showActionsMenu || actions ? 1 : 0);

    return (
      <HTMLTable
        striped={striped}
        interactive={interactive}
        bordered={bordered}
        condensed={condensed}
        stickyHeader={stickyHeader}
        ellipsisCells
        tableLayout="fixed"
        className={tableClass}
        role="table"
      >
        <Header
          dimensionColumns={dimensionColumns}
          valueColumns={valueColumns}
          showSparklines={showSparklines && results.length > 0 && results.some((row) => row.hasRawData)}
          showActionsMenu={showActionsMenu}
          actions={actions}
          allowSelection={allowSelection}
          onColumnClick={this.handleColumnClick}
          sortState={queryResultsCollection.sortState}
        />
        {results.length === 0 && bucket.fullyLoaded && this.renderNoResults(emptyStateColSpan)}
        <tbody>
          {loading &&
            results.length === 0 &&
            placeholders.map((d, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <tr key={index}>
                <td colSpan={emptyStateColSpan} className="bp4-skeleton" style={{ borderRadius: 0 }}>
                  Kentik
                </td>
              </tr>
            ))}
          {results.map((row, index) => (
            <Row
              {...this.props}
              key={this.getRowKey(row, dimensionColumns)}
              index={index}
              model={row}
              results={results}
              dimensionColumns={dimensionColumns}
              valueColumns={valueColumns}
              showSparklines={showSparklines && results.length > 0 && results.some((result) => result.hasRawData)}
              shouldAddDeviceColumn={this.shouldAddDeviceColumn(dimensionColumns)}
              mergeOverlayCells={mergeOverlayCells}
            />
          ))}
        </tbody>
      </HTMLTable>
    );
  }
}

export default ResultsTable;
